import styled, { DefaultTheme } from 'styled-components';

export const Form = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
`;

export const Field = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &:only-child {
    flex: 1 1 100%;
  }
`;

export const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.onBackground};
`;

export const Textarea = styled.textarea`
  min-height: 160px;
  resize: vertical;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => `${theme.colors.onBackground}50`};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.onSurface};
  font-size: 0.95rem;
  line-height: 1.4;
`;

export const HelperText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => `${theme.colors.onBackground}80`};
`;

export const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const InfoLabel = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => `${theme.colors.onBackground}90`};
`;

export const InfoValue = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.onSurface};
  word-break: break-word;
`;

export const MessageContent = styled.pre`
  margin-top: 0.5rem;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => `${theme.colors.onBackground}40`};
  background: ${({ theme }) => `${theme.colors.surface}`};
  color: ${({ theme }) => theme.colors.onSurface};
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const SectionTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.onBackground};
`;

export const SellersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => `${theme.colors.onBackground}30`};

  th,
  td {
    padding: 0.75rem;
    border-bottom: 1px solid ${({ theme }) => `${theme.colors.onBackground}20`};
    text-align: left;
    color: ${({ theme }) => theme.colors.onSurface};
    font-size: 0.9rem;
  }

  th {
    font-weight: 600;
    background: ${({ theme }) => `${theme.colors.onSurfaceLight}`};
    color: ${({ theme }) => theme.colors.onBackground};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

type StatusVariant = 'positive' | 'warning';

const getStatusColor = (theme: DefaultTheme, variant: StatusVariant) => {
  if (variant === 'positive') {
    return {
      background: `${theme.colors.positive}20`,
      color: theme.colors.positive,
    };
  }

  return {
    background: `${theme.colors.warning}33`,
    color: theme.colors.warning,
  };
};

export const StatusBadge = styled.span<{ variant: StatusVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  ${({ theme, variant }) => {
    const colors = getStatusColor(theme, variant);
    return `background: ${colors.background}; color: ${colors.color};`;
  }}
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${({ theme }) => `${theme.colors.onSurface}90`};
  font-size: 0.9rem;
`;
